/**
 * Reflections — slice 1 of epic #213 (issue #214).
 *
 * Andreas: "Identity and vision is about who you are, reflections don't
 * belong here." Mental Performance asks how today went — how focused you
 * were, what distracted you, how you handled pressure — which is a dated
 * observation about a moment, not a description of the cricketer you are.
 * It rendered under Identity & Vision until now purely because that is where
 * `player_identity` happened to put it.
 *
 * This is a PRESENTATION move, not a data move: the same five prompts and
 * five 1–10 scores, the same `mental_performance` section of the same
 * `player_identity` row, read and written over the same
 * `/players/me/identity` endpoint, under the same `data-testid`s. No schema
 * change, no new endpoint, and nothing a player already saved changes shape
 * or location. What changed is which tab it renders under.
 *
 * `strengths_weaknesses` deliberately did NOT come with it — technical /
 * tactical / mental strengths and weaknesses are self-awareness about who
 * you are, which is exactly what Identity & Vision is for.
 *
 * It loads the identity payload itself rather than receiving it from
 * PlayerIdentity.jsx: Radix unmounts inactive `TabsContent`, so the two tabs
 * are never mounted at the same time and there is no shared parent state to
 * pass down. Saving only `mental_performance` is safe — `upsertPlayerIdentity`
 * (api/src/db/repos/playerIdentity.ts) patches per section and leaves every
 * section absent from the payload exactly as it was.
 *
 * ---------------------------------------------------------------------------
 * SLICE 2 (issue #215) — the Monthly Check-In joined this page.
 * (It shipped as "Self-Awareness Check-In"; #384 renamed the LABEL only —
 * the route, the `/checkins` endpoint and every `ci-*` testid are
 * unchanged, per the #77 lesson: rename the label, not the path.)
 *
 * Unlike Mental Performance above, that one was NOT a presentation move.
 * The form existed in `PlayerIdentity.jsx` since the prototype and called
 * `/pi/checkins` — a path no router in this repo has ever mounted, and one
 * `functions/api/[[path]].ts` could not have routed even if it had, since it
 * only claims `/api/*`. So the form rendered, accepted nine answers, and
 * dropped every one of them into `useListCrud`'s `catch`. #215 built the
 * missing backend (`api/src/routes/checkins.ts`) and repointed the form.
 *
 * Same `ci-*` data-testids, deliberately: the harness and any journey that
 * targets them keeps working, and the diff reads as the move it is.
 *
 * WHAT A PLAYER SHOULD KNOW, and what the copy therefore says: check-ins are
 * NOT shared with a coach. The API has no route by which a coach or an admin
 * could read one (see `routes/checkins.ts` — there is no `player_id`
 * parameter to authorize). The copy says "not shared with your coaches" rather
 * than "only you will ever see this", for the same reason #205's coach
 * reflections do: an operator with database access can read the rows, so the
 * absolute claim would not be true.
 *
 * ---------------------------------------------------------------------------
 * SLICE 3 (issue #216) — match & season reflections joined this page.
 *
 * These are a THIRD thing again, and the difference that matters to a player
 * is the audience: match and season reflections ARE readable by a linked
 * coach (Andreas, 2026-08-17 — consistent with training reflections, which a
 * coach already reads in full), while the check-in above is not readable by
 * anybody but its author. Two opposite answers on one page is a real risk of
 * a player assuming the wrong one, so each section states its own audience in
 * its own copy, right next to the box they type into. If you change the
 * scoping in `api/src/routes/reflections.ts`, change the sentence here in the
 * same commit — an out-of-date privacy line is worse than none.
 *
 * The season entry point attaches to a season STRING (`matches.season` is
 * free text and a season entity is club-layer work, #158). The picker offers
 * the seasons the player actually has matches in (`GET /matches/seasons`) so
 * the common path is a click rather than a re-typing, but the field stays
 * free text because a player may want to reflect on a season before logging
 * anything in it. Case and stray whitespace fold server-side; a different
 * separator ("2025-26" vs "2025/26") does not — see the API's schema note.
 *
 * ---------------------------------------------------------------------------
 * SLICE 4 (issue #217) — the log & calendar arrived. It rendered at the TOP
 * of the page until #458 moved it to the BOTTOM; see the order note beside
 * the sections themselves.
 *
 * `components/ReflectionLog.jsx` renders one chronological view over all of
 * the above plus training reflections and coach feedback, tagged by kind,
 * from `GET /api/reflection-log`. It is a READ — every capture surface below
 * is unchanged and still owns its own writes.
 *
 * The three audience claims on this page now have a fourth reader to keep
 * consistent: the log's own scope note. A player's log contains their
 * check-ins; the same endpoint, read by a linked coach for this player,
 * does not (`api/src/routes/reflectionLog.ts` — the gate is the caller's
 * OWN player profile, not their role). If you change any audience rule,
 * change the copy in BOTH places in the same commit.
 *
 * ---------------------------------------------------------------------------
 * SLICE 5 (issue #852, epic #847 — voice wave V1) — the tab rebuilt to its
 * drawing, WITHOUT a microphone yet (that is #853, wired onto this rebuilt
 * tab next). It shipped behind a flag; D471 retired the old look and #1086
 * deleted the old branches, so what follows describes the one look.
 *
 * Q-D answered YES (D125, Andreas, 2026-09-25): `Reflect.dc.html`'s eleven
 * other drawn states — check-in editing/saving/save-failed; each section's
 * list loading and failed-to-load; match reflections empty/list/editing;
 * season reflection new/editing/refused — are built too, not just the face
 * ReflectVoice.dc.html itself draws. `ReflectMore.dc.html` holds the last
 * two (season reflection editing, refused) — the same drawing, split only
 * for the design canvas's own size limit (see both files' own header
 * comments).
 *
 * WHAT CHANGED — every item below is presentation:
 *
 *   - Section ORDER: the drawing puts Monthly check-in, then Season
 *     reflection, then Match reflections, ahead of the log — not the
 *     Match/Monthly/Season order #458 had chosen. Mental Performance stays
 *     first, so it sits outside the reordered block.
 *   - Section CHROME: each section's collapsed header (icon well, title,
 *     description, count/summary badge, chevron) is the kit's own look —
 *     `AccordionCard` (../components/AccordionCard.jsx) — not
 *     `identityForm.jsx`'s `SectionCard`. Its
 *     open/close behaviour, `toggleTestid`/`-count`/`-summary` contract and
 *     "exactly one of count or summary" invariant are unchanged (same
 *     `assertSectionHeaderState` guard). Mental Performance's own five
 *     prompts and score sliders are UNDRAWN here (ReflectVoice's own note:
 *     "the ui-map leaves its five sliders to a later decision") and stay
 *     exactly as shipped inside the new header shell.
 *   - The four check-in boxes, the season reflection box and a match
 *     reflection body each become a kit-styled multi-line box with an EMPTY
 *     `<testid>-footer` strip under it (#853's future mount point for the
 *     microphone — this slice leaves it empty, on purpose).
 *   - The check-in's self-rating becomes a tap-select 1–10 grid that starts
 *     UNSET (`Reflect.dc.html`'s own commentary: the old slider started
 *     at five and saved it whether or not anyone moved it — the same fault
 *     as a blank counter saved as a nought). The API already accepts
 *     `self_rating: null` (`checkins.ts`'s own schema), so this needs no
 *     schema change.
 *   - D55/D57/D109 apply: no failure toasts, no success toast where the
 *     screen itself already shows the save (the list updating IS the
 *     confirmation); a save failure stays on the page in ink, with the
 *     alert glyph and a rule, next to the button it belongs to; the three
 *     lists (check-ins, season and match reflections) now show a loading
 *     skeleton and a distinct failed-to-load card instead of silently
 *     rendering "nothing yet" on a swallowed error.
 *   - No gold anywhere on this tab (R-003) — every save is navy.
 *
 * The check-in's privacy line and the coach-can-read line are the words they
 * were before the rebuild — the one thing it must not touch even by a comma
 * (see SLICE 2/3 above on why).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { Textarea } from "../components/ui/textarea";
import { Q, SaveRow } from "../components/identityForm";
import { sectionFillSummary } from "../lib/sectionCardState";
import { Field, FieldError, ActionButton, SecondaryAction, DictationControl } from "../components/kit";
import { useFocusFirstInvalid } from "../lib/focusFirstInvalid";
import { getVoiceStatus } from "../lib/voice";
import ReflectionLog from "../components/ReflectionLog";
import AccordionCard from "../components/AccordionCard";
import ReflectionList from "../components/ReflectionList";
import {
  Brain, NotebookPen, Sparkles, Plus, Trash2, Lock, CalendarRange, ClipboardList, Eye,
  ChevronDown, Check, AlertCircle, CloudOff, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const SCORE_FIELDS = [
  { key: "confidence_score", label: "Confidence" },
  { key: "focus_score", label: "Focus" },
  { key: "energy_score", label: "Energy" },
  { key: "stress_score", label: "Stress" },
  { key: "match_readiness_score", label: "Match readiness" },
];

const empty = {
  focus_reflection: "", confidence_reflection: "", distractions: "", pressure_response: "", routines: "",
  confidence_score: 5, focus_score: 5, energy_score: 5, stress_score: 5, match_readiness_score: 5,
};

/**
 * The questions the folded header reports on (#458) — the five WRITTEN
 * prompts, and deliberately not the five scores. Every score is a slider
 * that ships at 5 and cannot be blank, so counting them would report five
 * answers a player has never given: "5 of 10" on a section nobody has
 * touched. The reflections are the part that is empty until it is written,
 * so they are the part that can honestly say so.
 */
const MP_PROMPTS = ["focus_reflection", "confidence_reflection", "distractions", "pressure_response", "routines"];

export default function Reflections() {
  const [mp, setMp] = useState(empty);
  const [saving, setSaving] = useState(false);

  // Issue #853 (epic #847, voice wave V3) — `getVoiceStatus()` fetched ONCE
  // per tab mount and fanned out to all six boxes'
  // `DictationControl`s below (each section reads it through its own
  // `voiceStatus` prop). Mirrors `LogMatchSavedStep.jsx`'s own "fetch once,
  // pass down" shape (#851) — this tab has six boxes sharing one screen
  // instead of one box on its own screen, so the fetch belongs up here,
  // not repeated per section or per box.
  const [voiceStatus, setVoiceStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getVoiceStatus().then((s) => {
      if (!cancelled) setVoiceStatus(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    api.get("/players/me/identity").then((r) => {
      setMp({ ...empty, ...(r.data.mental_performance || {}) });
    }).catch(() => {});
  }, []);

  const patch = (key, value) => setMp((d) => ({ ...d, [key]: value }));

  const mpSummary = sectionFillSummary(MP_PROMPTS.map((k) => mp[k]));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/players/me/identity", { mental_performance: mp });
      toast.success("Mental Performance saved");
    } catch (e) {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const mentalPerformanceBody = (
    <>
      <Q>How focused were you today?</Q>
      <Textarea rows={2} value={mp.focus_reflection} onChange={(e) => patch("focus_reflection", e.target.value)} data-testid="pi-mp-focus" />

      <Q>How confident are you currently?</Q>
      <Textarea rows={2} value={mp.confidence_reflection} onChange={(e) => patch("confidence_reflection", e.target.value)} data-testid="pi-mp-conf" />

      <Q>What distracted you?</Q>
      <Textarea rows={2} value={mp.distractions} onChange={(e) => patch("distractions", e.target.value)} data-testid="pi-mp-dist" />

      <Q>How did you respond to pressure?</Q>
      <Textarea rows={2} value={mp.pressure_response} onChange={(e) => patch("pressure_response", e.target.value)} data-testid="pi-mp-press" />

      <Q>Did you stick to your routines?</Q>
      <Textarea rows={2} value={mp.routines} onChange={(e) => patch("routines", e.target.value)} data-testid="pi-mp-routines" />

      {/* Issue #140: `md:grid-cols-5` (md = 768px) lands 5 columns at the
          exact width `Layout.jsx`'s sidebar (`hidden md:flex ... w-64`)
          also claims space — a native <input type="range"> has a
          rendering-level minimum width that doesn't shrink enough for 5
          columns in the ~500px left over, pushing the page 21px wide.
          Stepped up via an intermediate 2-column breakpoint so 5 columns
          only land at `lg:` (1024px), where there's room; `min-w-0` on the
          flex row is defence in depth against the range input's intrinsic
          width (same class of bug as Layout.jsx's `min-w-0`, issue #37).
          Moved here verbatim with the section (#214) — the breakpoint
          reasoning is about the sidebar and the range input, neither of
          which changed by rendering under a different tab. Undrawn by
          ReflectVoice.dc.html (#852) — left exactly as shipped. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 pt-2 border-t border-slate-100">
        {SCORE_FIELDS.map(({ key, label }) => (
          <div key={key}>
            <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div>
            <div className="flex items-center gap-2 min-w-0">
              <input type="range" min="1" max="10" value={mp[key] ?? 5}
                onChange={(e) => patch(key, parseInt(e.target.value, 10))}
                className="flex-1 min-w-0 accent-[#0B2E59]"
                data-testid={`pi-score-${key}`} />
              <span className="text-sm font-mono font-bold w-6 text-right" style={{ color: "#0B2E59" }}>{mp[key] ?? 5}</span>
            </div>
          </div>
        ))}
      </div>

      <SaveRow onSave={save} busy={saving} testid="pi-save-mp" />
    </>
  );

  return (
    // #852 fix round — the ink colour lives HERE, once, on the tab's own
    // root, not patched element by element on every surface that needs it. Every element this file renders (AccordionCard,
    // TextBox, the rating grid, the load-error/loading cards, the editors)
    // sets no colour of its own and inherits this — `--nw-ink` (#0F172A,
    // D27 and every one of the drawings), not the app-wide `body{color:
    // #0B1220}` default the kit README documents as a standing gap.
    // `ReflectionLog.jsx`'s own `rl-toggle` sits inside this same root and
    // picks the fix up for free — that file itself is untouched.
    <div className="space-y-6" data-testid="reflections-page" style={{ color: "var(--nw-ink)" }}>
      <div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-slate-500">
          <NotebookPen size={12} /> Reflections
        </div>
        <h2 className="font-display font-black text-2xl sm:text-3xl text-slate-900 mt-1">How it's actually going</h2>
        {/* #852 — ReflectVoice.dc.html/Reflect.dc.html draw this sentence with
            a lower-case "vision" (sentence case, matching the visual
            system's own tab label). */}
        <p className="text-slate-500 mt-1 max-w-2xl">Honest notes on where your head is at right now. Separate from who you are as a cricketer — that lives under Identity &amp; vision.</p>
      </div>

      {/* Issue #458 — Mental Performance folds like everything else on this
          page, asked for by Martin 2026-08-23 after using it. #378 had
          exempted it on the grounds that a fixed set of five prompts neither
          grows nor hides anything, so folding it only bought a click. What
          that reasoning missed is the PAGE: with the four sections below it
          folded, this one stayed the only open thing on a screen otherwise
          made of headers, and read as stuck rather than as deliberate. The
          consistency IS the feature — every section on Reflections is now a
          header with its own state on it, and the player opens the one they
          came for.

          Issue #852 — the header shell is `AccordionCard` (kit look); the five prompts, the score sliders and the save row
          inside it are as they always were (ReflectVoice.dc.html
          draws this section closed only — "the ui-map leaves its five
          sliders to a later decision"). */}
      <AccordionCard
        icon={Brain}
        title="Mental performance"
        desc="Where your head is at right now."
        summary={mpSummary}
        toggleTestid="mp-toggle"
        defaultOpen={false}
      >
        {mentalPerformanceBody}
      </AccordionCard>

      {/* Issue #378 — the folds, decided by Andreas 2026-08-20.
          Everything below this line that accumulates entries is COLLAPSIBLE
          and arrives FOLDED, with its COUNT on the header. Mental Performance
          above folds too since #458, but on a `summary` rather than a count —
          it is a fixed set of five prompts, so what it can honestly report is
          how many are answered, not how many exist.

          Issue #458 — the ORDER, superseding #378's, filed by Martin
          2026-08-23 after using the page. #378 put the log FIRST (#217's
          reasoning: it is the only thing here that answers "what have I
          written" rather than "write something") and ordered the capture
          surfaces Match, Season, Monthly. Martin asked for the opposite on
          both counts: the log at the BOTTOM, and Monthly above Season. Both
          asks are about a page you arrive at to WRITE — the capture surfaces
          come first and the log is where you go afterwards to look back.
          Folding is what makes the swap cheap: the log is a one-line header
          with a count wherever it sits, so being last costs a scroll, not a
          click.

          So that order was Match, Monthly, Season, then the log. Match keeps its own
          section rather than being absorbed into the log (#378) — this is
          the ONLY place a match reflection can be edited, so removing the
          section would silently remove editing.

          Issue #852 — the ORDER changed again, to match
          Reflect.dc.html/ReflectVoice.dc.html: Monthly, Season, Match, then
          the log (`Reflections.test.js` pins it). #1086 deleted the old order.

          NOTE for the harness: `frontend/e2e/dialog-config.js` lists all five
          toggles by testid, not by position, so the order here is free to
          change without touching that table.

          #853 — `voiceStatus` (fetched once, above) fans out to every
          section that mounts a box with a microphone; `ReflectionLog` has
          none and needs no prop. */}
      <CheckinsSection voiceStatus={voiceStatus} />
      <SeasonReflectionSection voiceStatus={voiceStatus} />
      <MatchReflectionsSection voiceStatus={voiceStatus} />
      <ReflectionLog kit />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*        Issue #852 — presentational primitives, local to this page          */
/*     (`AccordionCard` moved to ../components/AccordionCard.jsx, #965)       */
/* -------------------------------------------------------------------------- */

/**
 * TextBox — a kit-styled multi-line box with a footer strip that holds the
 * box's own `DictationControl` (#853, epic #847 voice wave V3). #852 left
 * this strip empty on purpose ("#853's future mount point") — this is that
 * point, filled.
 *
 * `idPrefix`/`boxLabel`/`maxLength`/`status` and the two `onRecordingChange`/
 * `onTranscribingChange` callbacks pass straight through to
 * `DictationControl` (#851) — see its own header for what each one means.
 * `onValueChange` is a SEPARATE prop from `onChange`: the textarea's own
 * `onChange` stays event-based (`(e) => …e.target.value`, unchanged from
 * #852), while `DictationControl`'s `onChange` needs the raw string, so a
 * transcript can be applied with the caller's OWN functional setter form
 * (`(v) => setDraft((d) => ({ ...d, field: v }))`, the executor spec's own
 * example) — never overwriting a sibling box's own in-flight update.
 *
 * `textareaRef` — reused, never a second `ref=` (#959): when the caller
 * passes one (`MatchReflectionsSection` does not need to; the check-in and
 * season sections don't either — only #935's WCAG focus-on-mount concern
 * applies to the saved step's own box, not this tab), it lands on the ONE
 * `ref={boxTextareaRef}` below, and the same ref object is handed to
 * `DictationControl` as `textareaRef` so it can return focus to the box
 * after an insert/undo. Every call site on this tab currently has nothing
 * else needing that ref, so an internal one is created when none is
 * passed.
 *
 * `dictationRef` — forwarded to `DictationControl`'s own imperative handle
 * only where a caller needs `discard()` (D157) — `MatchReflectionsSection`'s
 * "Cancel" is this tab's one "Not now"-shaped control; the check-in and
 * season sections have no cancel button, so they never pass this.
 */
function TextBox({
  testid, value, onChange, rows = 3, minHeight = 72, placeholder,
  idPrefix, boxLabel, maxLength, status, onValueChange,
  onRecordingChange, onTranscribingChange, textareaRef, dictationRef, voiceKey,
}) {
  const internalTextareaRef = useRef(null);
  const boxTextareaRef = textareaRef || internalTextareaRef;
  return (
    <div
      style={{
        display: "flex", flexDirection: "column", background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)", overflow: "hidden",
      }}
    >
      <textarea
        ref={boxTextareaRef}
        data-testid={testid}
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          minHeight, boxSizing: "border-box", padding: 12, width: "100%", resize: "vertical",
          border: "none", outline: "none", background: "transparent",
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif", fontSize: 15, lineHeight: 1.4, color: "var(--nw-ink)",
        }}
      />
      {/* #853's mount point, filled. `DictationControl` itself renders
          nothing (`null`) when voice is unavailable or not offered, so this
          strip stays visually empty wherever the pilot flag is off. */}
      <div
        data-testid={`${testid}-footer`}
        style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8,
          minHeight: 44, padding: 6, borderTop: "1px solid var(--nw-line-faint)",
        }}
      >
        <DictationControl
          key={voiceKey}
          ref={dictationRef}
          idPrefix={idPrefix}
          boxLabel={boxLabel}
          value={value}
          onChange={onValueChange}
          textareaRef={boxTextareaRef}
          onRecordingChange={onRecordingChange}
          onTranscribingChange={onTranscribingChange}
          status={status}
          maxLength={maxLength}
        />
      </div>
    </div>
  );
}

/**
 * RatingGrid — the check-in's self-rating, redrawn as a tap-select 1–10
 * grid that starts UNSET (see this file's header, "SLICE 5"). `testid` lands
 * on the grid container (`ci-rating`, kept — #852's testid contract); each
 * button carries its own `${testid}-${n}` so a journey can assert which one
 * is selected without relying on visual-only state.
 */
function RatingGrid({ testid, value, onChange }) {
  return (
    <div data-testid={testid} style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
        const selected = value === n;
        return (
          <button
            key={n}
            type="button"
            data-testid={`${testid}-${n}`}
            onClick={() => onChange(n)}
            aria-pressed={selected}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center", height: 40,
              borderRadius: 8, fontSize: 14, fontWeight: 700,
              background: selected ? "var(--nw-navy)" : "var(--nw-surface)",
              color: selected ? "#FFFFFF" : "var(--nw-ink)",
              border: selected ? "1px solid var(--nw-navy)" : "1px solid var(--nw-line)",
              cursor: "pointer",
            }}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

/** "2026-08" -> "August 2026", for the picked-month label. `MONTH_RE`-shaped
 * input only (native `<input type="month">` never emits anything else). */
function formatMonthLabel(monthStr) {
  const [y, m] = monthStr.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/**
 * MonthPickerField — Reflect.dc.html's own drawn `ci-month`: a blank
 * "Pick a month" placeholder row with a chevron, not a pre-filled value
 * (#852 fix round). A native `<input type="month">` cannot render custom
 * placeholder TEXT (a browser draws its own "mm/yyyy" hint, which is not
 * DOM text at all, so copy-check could never find "Pick a month" there) —
 * so the drawn row is a styled display div carrying the literal string,
 * with the real native input beneath it, transparent and exactly sized,
 * so the row is still a genuine `<input type="month">` for the OS/browser
 * picker AND directly fillable in a test the ordinary way (`data-testid`
 * on the input itself, not the display div — see the two ids below).
 */
function MonthPickerField({ testid, value, onChange, invalid = false }) {
  return (
    <div style={{ position: "relative" }}>
      {/* Purely visual — the real `<input>` below sits directly on top
          (later in DOM order, same stacking context) and is what actually
          receives every click/tap/keyboard interaction; this div carries
          no handler of its own, only the drawn look. */}
      <div
        data-testid={testid}
        aria-hidden="true"
        style={{
          minHeight: 46, boxSizing: "border-box", display: "flex", alignItems: "center", padding: "0 12px",
          justifyContent: "space-between", background: "var(--nw-surface)",
          // #1306 (D303): a missing month is the alert-red border, like every
          // other required field. The sentence under it stays ink.
          border: invalid ? "var(--nw-border-invalid)" : "1px solid var(--nw-line)",
          borderRadius: "var(--nw-radius-control)", fontSize: 15, lineHeight: 1.4,
          color: value ? "var(--nw-ink)" : "var(--nw-ink-muted)", cursor: "pointer",
        }}
      >
        <span>{value ? formatMonthLabel(value) : "Pick a month"}</span>
        <ChevronDown size={18} aria-hidden="true" style={{ color: "var(--nw-ink-muted)", flexShrink: 0 }} />
      </div>
      {/* The genuine control — transparent, exactly on top, so a click or
          tap anywhere in the row opens the OS/browser's own month picker,
          and a test can `.fill("2026-08")` it directly like any other
          input. Never `display: none`/`visibility: hidden` — either would
          make `showPicker()`/focus a no-op in some browsers. */}
      <input
        type="month"
        data-testid={`${testid}-input`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Month"
        aria-invalid={invalid ? true : undefined}
        aria-describedby={invalid ? `${testid}-error` : undefined}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", border: "none", padding: 0, margin: 0 }}
      />
    </div>
  );
}

/**
 * InlineError — D55's "field-error text stays ink" shape
 * (`components/LogMatchScorecardStep.jsx`'s own `FieldError`), for a
 * whole-form refusal that names no single field (`sr-error`,
 * Reflect.dc.html: "Both are required. The toast's own words, as a message
 * that stays above the button"). Ink text, the alert glyph, no toast.
 */
function InlineError({ testid, children }) {
  return (
    <div data-testid={testid} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "var(--nw-ink)" }}>
      <AlertCircle size={15} aria-hidden="true" style={{ flexShrink: 0 }} />
      {children}
    </div>
  );
}

/**
 * SaveFailedBanner — D57's "failures stay on the page, carrying the alert
 * glyph and rule" (Reflect.dc.html's `ci-save-error`: ink text, a plain
 * line — `--nw-line-strong`, not the alert-red left rule `ErrorBanner`
 * elsewhere in the app uses — and the alert-red glyph). Stands in for a
 * `toast.error("Save failed")`.
 */
function SaveFailedBanner({ testid, children }) {
  return (
    <div
      data-testid={testid}
      style={{
        display: "flex", gap: 8, padding: 12, background: "var(--nw-surface)",
        border: "1px solid var(--nw-line-strong)", borderRadius: "var(--nw-radius-control)",
        fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)",
      }}
    >
      <AlertCircle size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2, color: "var(--nw-alert)" }} />
      <span>{children}</span>
    </div>
  );
}

/**
 * LoadErrorCard — "a section whose list did not arrive says so and offers
 * to try again; it never shows its empty sentence" (Reflect.dc.html,
 * `ci-load-error`/`sr-load-error`/`mr-load-error`). The shipped page has no
 * failure state for these lists at all — a swallowed fetch failure used to
 * render identically to a genuinely empty section, the same #271 shape
 * `LoadFailureNote.jsx` fixed elsewhere; this is that fix's own drawn shape
 * for this tab, not a reuse of that component (this one is left-aligned,
 * not centred, per the drawing).
 */
function LoadErrorCard({ testid, what, onRetry }) {
  return (
    <div
      data-testid={testid}
      style={{
        display: "flex", flexDirection: "column", gap: 8, padding: 16,
        background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)",
      }}
    >
      <div className="flex items-center" style={{ gap: 10 }}>
        <CloudOff size={20} aria-hidden="true" style={{ color: "var(--nw-alert)", flexShrink: 0 }} />
        <div className="nw-body-strong">Couldn't load {what}</div>
      </div>
      <div className="nw-help">Nothing has been lost — we just couldn't reach the server.</div>
      <SecondaryAction
        data-testid={`${testid}-retry`}
        onClick={onRetry}
        style={{ alignSelf: "flex-start", width: "auto", padding: "0 14px", border: "1px solid var(--nw-line-strong)", color: "var(--nw-navy)" }}
      >
        <RefreshCw size={14} aria-hidden="true" style={{ marginRight: 6 }} />
        Try again
      </SecondaryAction>
    </div>
  );
}

/** Loading skeleton, two bars, matching `Reflect.dc.html`'s `ci-loading`. */
function ListLoadingSkeleton({ testid }) {
  return (
    <div data-testid={testid} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {[0, 1].map((i) => (
        <div
          key={i}
          style={{
            display: "flex", flexDirection: "column", gap: 8, padding: 14, height: 60, boxSizing: "border-box",
            borderRadius: "var(--nw-radius-card)", background: "var(--nw-surface)", border: "1px solid var(--nw-line)",
          }}
        >
          <div style={{ width: "55%", height: 12, borderRadius: 6, background: "var(--nw-line)" }} />
          <div style={{ width: "80%", height: 10, borderRadius: 5, background: "var(--nw-line-faint)" }} />
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*              Sub-module: the Monthly Check-In (#215, renamed #384)          */
/* -------------------------------------------------------------------------- */

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const emptyCheckin = {
  checkin_id: null, month: "",
  doing_well: "", avoiding: "", holding_back: "", focus_next: "",
  biggest_strength: "", biggest_challenge: "", next_priority: "", self_rating: null,
};

/**
 * An API check-in -> the draft shape, with nulls back to empty strings. A
 * check-in saved without a rating stays unrated (#852) — see this file's
 * header, "SLICE 5".
 */
function toDraft(c) {
  return {
    checkin_id: c.checkin_id,
    month: c.month,
    doing_well: c.doing_well || "",
    avoiding: c.avoiding || "",
    holding_back: c.holding_back || "",
    focus_next: c.focus_next || "",
    biggest_strength: c.biggest_strength || "",
    biggest_challenge: c.biggest_challenge || "",
    next_priority: c.next_priority || "",
    self_rating: c.self_rating ?? null,
  };
}

/**
 * The nine prompts. One card per month, and switching the month picker to a
 * month you have already written loads THAT check-in for editing rather than
 * offering a blank form that would overwrite it — which is what the API's
 * upsert-by-month rule looks like from up here. Save issues PATCH when the
 * draft is an existing row and POST otherwise; POST still upserts server-side,
 * so a stale tab cannot produce a second August.
 */
function CheckinsSection({ voiceStatus }) {
  // Issue #853, D157 (executor spec) — the check-in's save is disabled
  // while ANY of its four boxes records or transcribes, not just the one
  // most recently touched. One `{ recording, transcribing }` entry per
  // field, each updated only by that field's OWN `DictationControl`
  // through `micHandlers` below — a single shared boolean would let one
  // box finishing clear another box's still-live flag.
  const [micActivity, setMicActivity] = useState({
    doing_well: { recording: false, transcribing: false },
    avoiding: { recording: false, transcribing: false },
    holding_back: { recording: false, transcribing: false },
    focus_next: { recording: false, transcribing: false },
  });
  const micHandlers = (field) => ({
    onRecordingChange: (v) => setMicActivity((m) => ({ ...m, [field]: { ...m[field], recording: v } })),
    onTranscribingChange: (v) => setMicActivity((m) => ({ ...m, [field]: { ...m[field], transcribing: v } })),
  });
  const anyMicBusy = Object.values(micActivity).some((s) => s.recording || s.transcribing);

  const [items, setItems] = useState([]);
  // #852 fix round — the draft starts with NO month picked at all
  // (Reflect.dc.html's face: "Pick a month", not a pre-filled one) and the
  // self-rating unset. See this file's header.
  const [draft, setDraft] = useState({ ...emptyCheckin });
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [monthError, setMonthError] = useState(false);
  // #1306 (D303): Save with no month takes the player to the month. `attempt`
  // counts refused Saves so pressing Save again with the month still empty
  // goes back to it (NewGoalSheet's shape).
  const bodyRef = useRef(null);
  const [attempt, setAttempt] = useState(0);
  const focusErrors = useMemo(() => ({ month: monthError ? attempt : 0 }), [monthError, attempt]);
  const armFocus = useFocusFirstInvalid(bodyRef, focusErrors);
  const [listLoading, setListLoading] = useState(true);
  const [listFailed, setListFailed] = useState(false);

  const loadCheckins = () => {
    setListLoading(true);
    setListFailed(false);
    api.get("/checkins").then((r) => {
      const list = r.data || [];
      setItems(list);
      const thisMonth = list.find((c) => c.month === currentMonth());
      if (thisMonth) setDraft(toDraft(thisMonth));
      setListLoading(false);
    }).catch(() => {
      // `listLoading`/`listFailed` (#852) — see "Lists failed to load" in
      // Reflect.dc.html.
      setListLoading(false);
      setListFailed(true);
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCheckins(); }, []);

  const pickMonth = (month) => {
    setMonthError(false);
    const existing = items.find((c) => c.month === month);
    setDraft(existing ? toDraft(existing) : { ...emptyCheckin, month });
  };

  const edit = (c) => setDraft(toDraft(c));

  const submit = async () => {
    armFocus();
    // #852 fix round — the month is the one field this form cannot leave
    // blank (the API's own `month: z.string().regex(MONTH_RE)`, no
    // default): the drawing draws a blank picker, so this validation
    // refuses a Save with none picked.
    if (!draft.month) {
      setMonthError(true);
      setAttempt((n) => n + 1);
      return;
    }
    setMonthError(false);
    setSaving(true);
    setSaveFailed(false);
    try {
      // Every field is optional server-side; empty strings arrive as nulls.
      // Nothing here refuses to save a half-finished check-in.
      const payload = {
        doing_well: draft.doing_well, avoiding: draft.avoiding,
        holding_back: draft.holding_back, focus_next: draft.focus_next,
        biggest_strength: draft.biggest_strength, biggest_challenge: draft.biggest_challenge,
        next_priority: draft.next_priority, self_rating: draft.self_rating,
      };
      const r = draft.checkin_id
        ? await api.patch(`/checkins/${draft.checkin_id}`, payload)
        : await api.post("/checkins", { ...payload, month: draft.month });
      const saved = r.data;
      setItems((prev) => [saved, ...prev.filter((c) => c.checkin_id !== saved.checkin_id)]
        .sort((a, b) => b.month.localeCompare(a.month)));
      setDraft(toDraft(saved));
      // D57/D109 (#852): the list updating IS the confirmation — no success
      // toast.
    } catch (e) {
      // D55/D57 (#852): the failure stays on the page, in ink, next to the
      // button — no toast.
      setSaveFailed(true);
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/checkins/${id}`);
      setItems((prev) => prev.filter((c) => c.checkin_id !== id));
      setDraft((d) => (d.checkin_id === id ? { ...emptyCheckin, month: d.month } : d));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const privacyNotice = (
    <div
      className="flex items-start gap-2 rounded-lg p-3"
      style={{ background: "var(--nw-navy-tint)", fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}
      data-testid="ci-privacy"
    >
      <Lock size={14} className="mt-0.5 shrink-0" style={{ color: "var(--nw-navy)" }} aria-hidden="true" />
      <span>These answers are yours. Check-ins are <b>not shared with your coaches</b> — nothing in the app shows them to anyone else. Answer as honestly as you can; a half-finished check-in saves fine.</span>
    </div>
  );

  const promptsList = (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div className="nw-section">Reflection prompts</div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.6, color: "var(--nw-ink-lede)" }}>
        <li>What am I doing well?</li>
        <li>What am I avoiding?</li>
        <li>What is holding me back?</li>
        <li>What should I focus on next?</li>
      </ul>
    </div>
  );

  const SaveIcon = saving ? null : draft.checkin_id ? Check : Plus;
  const saveLabel = saving ? "Saving…" : draft.checkin_id ? "Update check-in" : "Save check-in";

  const body = (
    <div className="space-y-3" ref={bodyRef}>
      {privacyNotice}

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div className="nw-label">Month</div>
        <MonthPickerField testid="ci-month" value={draft.month} onChange={pickMonth} invalid={monthError} />
        {monthError && <FieldError id="ci-month-error" testId="ci-month-error">Pick a month first</FieldError>}
      </div>

      {promptsList}

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div className="nw-label">Doing well</div>
        <TextBox
          testid="ci-well" minHeight={72} value={draft.doing_well}
          onChange={(e) => setDraft({ ...draft, doing_well: e.target.value })}
          onValueChange={(v) => setDraft((d) => ({ ...d, doing_well: v }))}
          idPrefix="ci-well-mic" boxLabel="Doing well" maxLength={4000} status={voiceStatus}
          {...micHandlers("doing_well")} voiceKey={draft.checkin_id ?? `new:${draft.month}`}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div className="nw-label">Avoiding</div>
        <TextBox
          testid="ci-avoid" minHeight={72} value={draft.avoiding}
          onChange={(e) => setDraft({ ...draft, avoiding: e.target.value })}
          onValueChange={(v) => setDraft((d) => ({ ...d, avoiding: v }))}
          idPrefix="ci-avoid-mic" boxLabel="Avoiding" maxLength={4000} status={voiceStatus}
          {...micHandlers("avoiding")} voiceKey={draft.checkin_id ?? `new:${draft.month}`}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div className="nw-label">Holding me back</div>
        <TextBox
          testid="ci-holdback" minHeight={72} value={draft.holding_back}
          onChange={(e) => setDraft({ ...draft, holding_back: e.target.value })}
          onValueChange={(v) => setDraft((d) => ({ ...d, holding_back: v }))}
          idPrefix="ci-holdback-mic" boxLabel="Holding me back" maxLength={4000} status={voiceStatus}
          {...micHandlers("holding_back")} voiceKey={draft.checkin_id ?? `new:${draft.month}`}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div className="nw-label">Focus next</div>
        <TextBox
          testid="ci-next" minHeight={72} value={draft.focus_next}
          onChange={(e) => setDraft({ ...draft, focus_next: e.target.value })}
          onValueChange={(v) => setDraft((d) => ({ ...d, focus_next: v }))}
          idPrefix="ci-next-mic" boxLabel="Focus next" maxLength={4000} status={voiceStatus}
          {...micHandlers("focus_next")} voiceKey={draft.checkin_id ?? `new:${draft.month}`}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div className="nw-label">Biggest strength this month</div>
        <Field value={draft.biggest_strength} onChange={(e) => setDraft({ ...draft, biggest_strength: e.target.value })} data-testid="ci-strength" />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div className="nw-label">Biggest challenge this month</div>
        <Field value={draft.biggest_challenge} onChange={(e) => setDraft({ ...draft, biggest_challenge: e.target.value })} data-testid="ci-challenge" />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div className="nw-label">Next month's priority</div>
        <Field value={draft.next_priority} onChange={(e) => setDraft({ ...draft, next_priority: e.target.value })} data-testid="ci-priority" />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div className="nw-label">Self-rating, 1 low to 10 peak <span style={{ fontWeight: 400 }}>(optional)</span></div>
        <RatingGrid testid="ci-rating" value={draft.self_rating} onChange={(n) => setDraft({ ...draft, self_rating: n })} />
        <div className="nw-help">None picked until you tap one. A score nobody chose is not saved as a five.</div>
      </div>

      {saveFailed && <SaveFailedBanner testid="ci-save-error">Couldn't save your check-in. Nothing you typed is lost — try again.</SaveFailedBanner>}

      <ActionButton
        data-testid="ci-add"
        icon={SaveIcon ? <SaveIcon size={16} aria-hidden="true" /> : null}
        onClick={submit}
        // Issue #853, D157 (executor spec) — disabled while saving OR
        // while any of the four boxes records or transcribes.
        disabled={saving || anyMicBusy}
        style={{ background: saving ? "var(--nw-line-strong)" : "var(--nw-navy)", color: saving ? "var(--nw-ink-muted)" : "#FFFFFF" }}
      >
        {saveLabel}
      </ActionButton>

      {items.length > 0 && (
        <div className="space-y-2 pt-3 border-t border-slate-100" data-testid="ci-list">
          {/* #852 fix round — `Reflect.dc.html`'s own section label above the
              list ("Earlier check-ins"). */}
          <div className="nw-section">Earlier check-ins</div>
          {items.map((it) => (
            <div key={it.checkin_id} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="font-semibold text-slate-900">{it.month}</span>
                    {it.self_rating != null && (
                      // #852 fix round — R-003: the old badge was
                      // gold-tinted (`#D4A017` family), a real violation
                      // caught only once a rated check-in was actually in
                      // this list during a runtime gold sweep (journey 36's
                      // own sweep never had one). Navy-tinted. The
                      // drawing's own row reads "[MONTH] · Self-rating
                      // X/10" — the middot is literal, not a gap.
                      <span>
                        <span aria-hidden="true"> · </span>
                        <span className="px-2 py-0.5 rounded-full font-semibold" style={{ background: "var(--nw-navy-tint)", color: "var(--nw-navy)" }}>Self-rating {it.self_rating}/10</span>
                      </span>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-2 mt-2 text-sm">
                    {it.biggest_strength && <div><b className="text-slate-600">Strength:</b> {it.biggest_strength}</div>}
                    {it.biggest_challenge && <div><b className="text-slate-600">Challenge:</b> {it.biggest_challenge}</div>}
                    {it.next_priority && <div className="md:col-span-2"><b className="text-slate-600">Next month:</b> {it.next_priority}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => edit(it)} className="text-xs font-semibold text-[#0B2E59] hover:underline" data-testid={`ci-edit-${it.checkin_id}`}>
                    Edit
                  </button>
                  <button onClick={() => remove(it.checkin_id)} className="text-slate-400 hover:text-red-500 transition" data-testid={`ci-del-${it.checkin_id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // #852 — lists loading / failed to load (Reflect.dc.html), distinct from
  // genuinely empty (the old page rendered a failed fetch identically to
  // zero saved check-ins).
  return (
    <AccordionCard
      icon={Sparkles}
      title="Monthly check-in"
      desc="A ritual to honestly review the last 30 days."
      defaultOpen={false}
      count={listLoading ? "…" : listFailed ? "—" : items.length}
      toggleTestid="ci-toggle"
    >
      {listLoading ? (
        <ListLoadingSkeleton testid="ci-loading" />
      ) : listFailed ? (
        <LoadErrorCard testid="ci-load-error" what="your check-ins" onRetry={loadCheckins} />
      ) : (
        body
      )}
    </AccordionCard>
  );
}

/* -------------------------------------------------------------------------- */
/*            Sub-modules: season and match reflections (#216)                */
/* -------------------------------------------------------------------------- */

/**
 * The one sentence a player must not be wrong about on this page.
 *
 * Rendered on BOTH #216 sections, not once at the top: the check-in above
 * carries the opposite claim ("not shared with your coaches"), and a single
 * banner somewhere on a long page is exactly how a player ends up applying
 * the wrong one to the box they are typing in.
 *
 * "A linked coach" is literal — a coach with an ACTIVE link, which is a
 * relationship the player accepts and can revoke (`/api/coach-links`). It
 * does NOT say "only your coach", for the same reason #205's copy doesn't:
 * an operator with database access can read the rows, so the absolute claim
 * would not be true.
 *
 * #852 — the drawn navy-tinted notice (R-003: no gold on this tab).
 */
function CoachVisibleNotice({ testid }) {
  return (
    <div
      className="flex items-start gap-2 rounded-lg p-3"
      style={{ background: "var(--nw-navy-tint)", fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}
      data-testid={testid}
    >
      <Eye size={14} className="mt-0.5 shrink-0" style={{ color: "var(--nw-navy)" }} aria-hidden="true" />
      <span>
        A coach you are linked to <b>can read this</b> — same as your training reflections. Write it for
        yourself first; it is still your record, not a report.
      </span>
    </div>
  );
}

/**
 * Season reflection — the entry point #216 asks for under this tab.
 *
 * One reflection per season, enforced by the API (a unique index on the
 * normalised key), so picking a season you have already written about loads
 * THAT reflection for editing rather than offering a blank box that would
 * overwrite it — the same rule the check-in's month picker follows, and for
 * the same reason.
 */
function SeasonReflectionSection({ voiceStatus }) {
  const [items, setItems] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [draft, setDraft] = useState({ reflection_id: null, season: "", body: "" });
  const [saving, setSaving] = useState(false);
  const [refused, setRefused] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [listFailed, setListFailed] = useState(false);
  // Issue #853, D157 (executor spec) — `sr-save` is disabled while
  // `sr-body` records or transcribes, one box so one pair of booleans.
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const loadSeasonReflections = () => {
    setListLoading(true);
    setListFailed(false);
    api.get("/reflections?kind=season").then((r) => {
      setItems(r.data || []);
      setListLoading(false);
    }).catch(() => {
      setListLoading(false);
      setListFailed(true);
    });
  };

  useEffect(() => {
    loadSeasonReflections();
    // The player's real seasons, from their own matches. Suggestions only —
    // the field stays free text so a season with no matches logged yet can
    // still be reflected on.
    api.get("/matches/seasons").then((r) => setSeasons(r.data?.seasons || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickSeason = (season) => {
    const key = season.trim().replace(/\s+/g, " ").toLowerCase();
    const existing = items.find((r) => r.season_key === key);
    setDraft(existing
      ? { reflection_id: existing.reflection_id, season: existing.season, body: existing.body }
      : { reflection_id: null, season, body: "" });
  };

  const edit = (r) => setDraft({ reflection_id: r.reflection_id, season: r.season, body: r.body });

  const save = async () => {
    if (!draft.season.trim() || !draft.body.trim()) {
      setRefused(true);
      return;
    }
    setRefused(false);
    setSaving(true);
    try {
      // POST always: the API upserts on the normalised season, so a stale
      // tab cannot produce a second 2025/26.
      const r = await api.post("/reflections", { kind: "season", season: draft.season, body: draft.body });
      const saved = r.data;
      setItems((prev) => [saved, ...prev.filter((x) => x.reflection_id !== saved.reflection_id)]);
      setDraft({ reflection_id: saved.reflection_id, season: saved.season, body: saved.body });
    } catch (e) {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/reflections/${id}`);
      setItems((prev) => prev.filter((r) => r.reflection_id !== id));
      setDraft((d) => (d.reflection_id === id ? { reflection_id: null, season: d.season, body: "" } : d));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const saveLabel = saving ? "Saving…" : draft.reflection_id ? "Update reflection" : "Save reflection";

  const body = (
    <div className="space-y-3">
      <CoachVisibleNotice testid="sr-visibility" />

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div className="nw-label">Season</div>
        <Field
          list="season-reflection-suggestions"
          value={draft.season}
          onChange={(e) => pickSeason(e.target.value)}
          placeholder="e.g. 2025/26"
          data-testid="sr-season"
        />
        <datalist id="season-reflection-suggestions">
          {seasons.map((s) => <option key={s} value={s} />)}
        </datalist>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div className="nw-label">How was the season?</div>
        <TextBox
          testid="sr-body"
          minHeight={110}
          rows={4}
          value={draft.body}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          onValueChange={(v) => setDraft((d) => ({ ...d, body: v }))}
          placeholder="What changed, what you'd do differently, what you're taking into next season."
          idPrefix="sr-body-mic"
          voiceKey={draft.reflection_id ?? `new:${draft.season}`}
          boxLabel="How was the season?"
          maxLength={8000}
          status={voiceStatus}
          onRecordingChange={setRecording}
          onTranscribingChange={setTranscribing}
        />
      </div>

      {refused && <InlineError testid="sr-error">Pick a season and write something first</InlineError>}

      <ActionButton data-testid="sr-save" onClick={save} disabled={saving || recording || transcribing} style={{ background: "var(--nw-navy)" }}>
        {saveLabel}
      </ActionButton>

      <ReflectionList
        items={items}
        prefix="sr"
        label={(r) => r.season}
        onEdit={edit}
        onDelete={remove}
        kit
      />
    </div>
  );

  return (
    <AccordionCard
      icon={CalendarRange}
      title="Season reflection"
      desc="Stand back at the end of a season and say what it was."
      defaultOpen={false}
      count={listLoading ? "…" : listFailed ? "—" : items.length}
      toggleTestid="sr-toggle"
    >
      {listLoading ? (
        <ListLoadingSkeleton testid="sr-loading" />
      ) : listFailed ? (
        <LoadErrorCard testid="sr-load-error" what="your season reflections" onRetry={loadSeasonReflections} />
      ) : (
        body
      )}
    </AccordionCard>
  );
}

/**
 * Match reflections — READ AND EDIT ONLY. There is no "write a reflection
 * about a match" composer here on purpose: choosing a match out of a list of
 * every match you have ever played is a worse gesture than the one #216
 * actually asks for, which is being offered the box straight after logging
 * the innings (PerformanceTracking.jsx). This section is the "editable and
 * readable later" half of that.
 */
function MatchReflectionsSection({ voiceStatus }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // { reflection_id, body, match_opponent, match_date }
  const [saving, setSaving] = useState(false);
  const [refused, setRefused] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [listFailed, setListFailed] = useState(false);
  // Issue #853, D157 (executor spec) — `mr-save` is disabled while
  // `mr-body` records or transcribes. `dictationRef` is the box's own
  // `DictationControl` imperative handle — "Cancel" (this section's one
  // "Not now"-shaped control) calls `discard()` on it before dropping the
  // edit, same as `ReflectionPrompt.jsx`'s own "Not now" does (#851).
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const dictationRef = useRef(null);

  const loadMatchReflections = () => {
    setListLoading(true);
    setListFailed(false);
    api.get("/reflections?kind=match").then((r) => {
      setItems(r.data || []);
      setListLoading(false);
    }).catch(() => {
      setListLoading(false);
      setListFailed(true);
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadMatchReflections(); }, []);

  const save = async () => {
    if (!editing?.body.trim()) {
      setRefused(true);
      return;
    }
    setRefused(false);
    setSaving(true);
    try {
      const r = await api.patch(`/reflections/${editing.reflection_id}`, { body: editing.body });
      const saved = r.data;
      setItems((prev) => prev.map((x) => (x.reflection_id === saved.reflection_id ? saved : x)));
      setEditing(null);
    } catch (e) {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/reflections/${id}`);
      setItems((prev) => prev.filter((r) => r.reflection_id !== id));
      setEditing((e) => (e?.reflection_id === id ? null : e));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const emptyCopy = "Nothing here yet. Log a match under Your matches and you'll be offered the box — it's always optional, and it never blocks saving the innings.";

  const editingLabel = [editing?.match_opponent, editing?.match_date].filter(Boolean).join(" · ");

  const body = (
    <div className="space-y-3">
      <CoachVisibleNotice testid="mr-visibility" />

      {items.length === 0 && !editing && (
        <p className="nw-body" data-testid="mr-empty">
          {emptyCopy}
        </p>
      )}

      {editing && (
        <div data-testid="mr-editor" style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-control)" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div className="nw-label">{editingLabel ? `Editing · ${editingLabel}` : "Editing"}</div>
            <TextBox
              testid="mr-body" minHeight={110} rows={4} value={editing.body}
              onChange={(e) => setEditing({ ...editing, body: e.target.value })}
              onValueChange={(v) => setEditing((d) => ({ ...d, body: v }))}
              idPrefix="mr-body-mic" boxLabel="Editing" maxLength={8000} status={voiceStatus}
              onRecordingChange={setRecording} onTranscribingChange={setTranscribing}
              dictationRef={dictationRef} voiceKey={editing.reflection_id}
            />
          </div>
          {refused && <InlineError testid="mr-error">Write something first</InlineError>}
          <div className="flex gap-2">
            <SecondaryAction
              variant="navy-outline"
              data-testid="mr-cancel"
              onClick={() => {
                // D157 — drop the mic's own recording/in-flight request
                // BEFORE closing the editor, same order "Not now" uses on
                // the saved step (#851): nothing this control started is
                // left orphaned.
                dictationRef.current?.discard();
                setEditing(null);
                setRefused(false);
              }}
              style={{ flex: 1 }}
            >
              Cancel
            </SecondaryAction>
            <ActionButton data-testid="mr-save" onClick={save} disabled={saving || recording || transcribing} style={{ flex: 1, background: "var(--nw-navy)" }}>
              {saving ? "Saving…" : "Update reflection"}
            </ActionButton>
          </div>
        </div>
      )}
    </div>
  );

  const onEdit = (r) => setEditing({ reflection_id: r.reflection_id, body: r.body, match_opponent: r.match_opponent, match_date: r.match_date });

  const listSection = (
    <ReflectionList
      items={items}
      prefix="mr"
      label={(r) => [r.match_opponent, r.match_date].filter(Boolean).join(" · ") || "Match"}
      onEdit={onEdit}
      onDelete={remove}
      kit
    />
  );

  return (
    <AccordionCard
      icon={ClipboardList}
      title="Match reflections"
      desc="What you wrote after an innings. Add one when you log a match."
      defaultOpen={false}
      count={listLoading ? "…" : listFailed ? "—" : items.length}
      toggleTestid="mr-toggle"
    >
      {listLoading ? (
        <ListLoadingSkeleton testid="mr-loading" />
      ) : listFailed ? (
        <LoadErrorCard testid="mr-load-error" what="your match reflections" onRetry={loadMatchReflections} />
      ) : (
        <>
          {body}
          {listSection}
        </>
      )}
    </AccordionCard>
  );
}
